import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { AppShell } from "../components/layout/AppShell";
import { NeuButton } from "../components/neu/Neu";
import { usePrefs } from "../context/PrefsContext";
import { liveMap } from "../api/liveMap";
import { useLiveMap } from "../features/map/useLiveMap";
import { LivePeopleList } from "../features/live/LivePeopleList";
import { LiveStats } from "../features/live/LiveStats";

const REFRESH_MS = 30000;

export default function LiveMapPage() {
  const { t, i18n } = useTranslation();
  const { theme } = usePrefs();
  const container = useRef(null);
  const [data, setData] = useState(null);
  const [at, setAt] = useState("");
  const { error, focus } = useLiveMap({ container, data, lang: i18n.language, theme });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const next = await liveMap();
        if (!alive) return;
        setData(next);
        setAt(new Date().toTimeString().slice(0, 8));
      } catch {
        /* keep the last snapshot on a transient network error */
      }
    };
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  const refresh = async () => {
    setData(await liveMap());
    setAt(new Date().toTimeString().slice(0, 8));
  };

  return (
    <AppShell
      title={t("live.title")}
      subtitle={t("live.subtitle")}
      actions={
        <NeuButton data-testid="live-refresh" onClick={refresh}>
          <RefreshCw className="h-4 w-4" /> {at ? <span dir="ltr">{at}</span> : t("live.refresh")}
        </NeuButton>
      }
    >
      <div className="grid gap-6" data-testid="live-map-page">
        <LiveStats totals={data?.totals} date={data?.date || "—"} />
        <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
          <div
            ref={container}
            dir="ltr"
            data-testid="live-map-canvas"
            className="h-[26rem] w-full overflow-hidden rounded-3xl border border-border/60 shadow-neu-in lg:h-[32rem]"
          />
          <LivePeopleList people={data?.people || []} onFocus={focus} />
        </div>
        {error ? (
          <p className="text-[11px] font-bold text-destructive" data-testid="live-map-error">{t("map.loadError")}</p>
        ) : null}
      </div>
    </AppShell>
  );
}
